import { useQuery } from '@apollo/client/react'
import { Link } from '@/components/LocalizedLink'
import { MapPin, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GET_FOOTER_QUERY } from '@/api/getFooter'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useThemeStore } from '@/store/theme'
import { FormEvent, useMemo, useState } from 'react'
import { NavLinkItem, NavLinkItemValue, SocialLinkItem } from '@/gql/schema'
import { resolveNavLink } from '@/utils/navigation'
import Container from '@/components/Container'

export default function Footer() {
  const { t } = useTranslation()
  const tenantTheme = useThemeStore((state) => state.tenantTheme)
  const { data: footerData } = useQuery(GET_FOOTER_QUERY, {
    variables: {
      code: 'main-footer'
    }
  })
  const footerAddress = footerData?.getFooter?.address

  const [newsLetterErrorMessage, setNewsLetterErrorMessage] = useState<string | undefined>(undefined)

  const footerLinks = useMemo(() => {
    const navLinks = footerData?.getFooter?.navLinks
    return navLinks
      ? navLinks
        .filter((link: NavLinkItem) => link.active)
        .map((link: NavLinkItem) => ({
          label: link.label,
          children: link.children?.filter((child: NavLinkItemValue) => child.active)
        }))
      : []
  }, [footerData])

  const socialLinks = useMemo(() => {
    const socialLinks = footerData?.getFooter?.socialLinks
    return socialLinks
      ? socialLinks
        .filter((link: SocialLinkItem) => link.active)
      : []
  }, [footerData])

  // Split nav links into two columns for the middle section
  const midPoint = Math.ceil(footerLinks.length / 2)
  const col1Links = footerLinks.slice(0, midPoint)
  const col2Links = footerLinks.slice(midPoint)



  return (
    <footer className="w-full border-t border-secondary/10 bg-background pt-16 pb-12 mt-auto">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

          {/* Column 1: Brand & Contact Info */}
          <div className="flex flex-col gap-8">
            <Link to="/" className="inline-block">
              <img
                src={tenantTheme.logoUrl}
                alt={`${tenantTheme.name} logo`}
                className="h-8 object-contain"
                width={160}
                height={32}
                loading="lazy"
              />
            </Link>

            <div className="flex flex-col gap-6 text-sm text-foreground/80">
              {footerAddress && <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                <p className="leading-relaxed text-foreground whitespace-pre-line">{footerAddress}</p>
              </div>}

              {/* <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">+351 229 982 500</p>
                  <p className="text-xs text-foreground/70 mt-1">Chamada para a rede fixa nacional</p>
                </div>
              </div> */}

              {/* <p className="text-[11px] text-muted leading-relaxed max-w-[200px]">
                Aberto de segunda a sexta-feira das
                9:00 às 13:00 e das 14:30 às 18:30.
              </p> */}
            </div>
          </div>

          {/* Column 2: Navigation Links Part 1 */}
          {col1Links.length > 0 && (
            <div className="flex flex-col gap-8">
              {col1Links.map((group, idx) => (
                <div key={idx} className="flex flex-col gap-4">
                  <h2 className="font-semibold text-secondary">{group.label}</h2>
                  {group.children && group.children.length > 0 && (
                    <ul className="flex flex-col gap-3">
                      {group.children.map((link: NavLinkItemValue, linkIdx: number) => {
                        const resolvedLink = resolveNavLink(link)
                        return (
                          <li key={linkIdx}>
                            <Link
                              to={resolvedLink.url}
                              target={resolvedLink.isExternal ? '_blank' : undefined}
                              rel={resolvedLink.isExternal ? 'noopener noreferrer' : undefined}
                              className="text-sm text-foreground/80 hover:text-primary-text transition-colors flex items-center"
                            >
                              {link.label} {resolvedLink.isExternal && <ExternalLink className="w-2 h-2 ml-1" />}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Column 3: Navigation Links Part 2 */}
          {col2Links.length > 0 && (
            <div className="flex flex-col gap-8">
              {col2Links.map((group, idx) => (
                <div key={idx} className="flex flex-col gap-4">
                  <h2 className="font-semibold text-secondary">{group.label}</h2>
                  {group.children && group.children.length > 0 && (
                    <ul className="flex flex-col gap-3">
                      {group.children.map((link: NavLinkItemValue, linkIdx: number) => {
                        const resolvedLink = resolveNavLink(link)
                        return (
                          <li key={linkIdx}>
                            <Link
                              to={resolvedLink.url}
                              target={resolvedLink.isExternal ? '_blank' : undefined}
                              rel={resolvedLink.isExternal ? 'noopener noreferrer' : undefined}
                              className="text-sm text-foreground/80 hover:text-primary-text transition-colors flex items-center"
                            >
                              {link.label} {resolvedLink.isExternal && <ExternalLink className="w-2 h-2 ml-1" />}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Column 4: Social & Newsletter */}
          <div className="flex flex-col gap-10">
            {/* Newsletter */}
            <div className="flex flex-col gap-4">
              <h2 className="font-semibold text-secondary">{t('footer.newsletterTitle')}</h2>
              <p className="text-sm font-medium text-foreground leading-snug">
                {t('footer.newsletterCopyLine1')}<br />
                {t('footer.newsletterCopyLine2')}
              </p>
              <form className="flex mt-2" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder={t('footer.newsletterPlaceholder')}
                  className="flex-1 min-w-0 px-4 py-2.5 text-sm bg-transparent border border-secondary/20 rounded-l-lg focus:outline-none focus:border-primary transition-colors"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 text-sm font-medium text-on-primary bg-primary rounded-r-lg hover:bg-primary/90 transition-colors shrink-0 cursor-pointer"
                  onClick={(event: FormEvent<HTMLButtonElement>) => {
                    event.preventDefault()
                    setNewsLetterErrorMessage(t('footer.newsletterError'))
                  }}
                >
                  {t('footer.subscribe')}
                </button>
              </form>
              {newsLetterErrorMessage && <p className="text-sm text-red-500">{newsLetterErrorMessage}</p>}
            </div>

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className="flex flex-col gap-4">
                <h2 className="font-semibold text-secondary">{t('footer.joinUs')}</h2>
                <div className="flex items-center gap-4">
                  {socialLinks.map((social: SocialLinkItem, idx: number) => (
                    <Link
                      key={idx}
                      to={social.url}
                      className="text-foreground hover:text-primary-text transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {social.imageUrl && (
                        <img
                          src={social.imageUrl}
                          alt={`${social.label} icon`}
                          className="w-5 h-5 object-contain"
                          width={40}
                          height={40}
                          loading="lazy"
                        />
                      )}
                      {!social.imageUrl && <span className="text-sm">{social.label}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        <div className="mt-12 pt-8 border-t border-secondary/10">
          <LanguageSwitcher />
        </div>
      </Container>
    </footer>
  )
}
